import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { getKeycloakInstance, getKeycloakSync } from '@/configs/keycloak.config';
import { useCorsErrorStore } from '@/store/zustand/cors-error.store';
import { isUnauthorizedError } from '@/utils/get-api-error-message';
import { isCorsOrNetworkError } from '@/utils/is-cors-or-network-error';
import { getKeycloakLoginOptions } from '@/utils/keycloak-login';

const notifyCorsError = (error: unknown) => {
  if (isCorsOrNetworkError(error)) {
    useCorsErrorStore.getState().showCorsErrorModal();
  }
};

// A single 401 can fan out across many in-flight queries/mutations; guard so we
// only kick off one Keycloak redirect instead of stomping it on every error.
let isRedirectingToLogin = false;

/**
 * On a 401 from any query/mutation, send the user to the Keycloak login page
 * (e.g. their session/token expired). After a successful login Keycloak returns
 * them to the page they were on.
 */
const redirectToLoginOnUnauthorized = (error: unknown) => {
  if (typeof window === 'undefined' || isRedirectingToLogin) {
    return;
  }

  if (!isUnauthorizedError(error)) {
    return;
  }

  const keycloak = getKeycloakSync();
  if (keycloak) {
    isRedirectingToLogin = true;
    keycloak.login(getKeycloakLoginOptions({ redirectUri: window.location.href }));
    return;
  }

  void getKeycloakInstance().then(instance => {
    if (!instance || isRedirectingToLogin) return;
    isRedirectingToLogin = true;
    instance.login(getKeycloakLoginOptions({ redirectUri: window.location.href }));
  });
};

const handleQueryClientError = (error: unknown) => {
  notifyCorsError(error);
  redirectToLoginOnUnauthorized(error);
};

const clientsWithCorsHandler = new WeakSet<AxiosInstance>();

export const setupAxiosCorsErrorHandler = (client: AxiosInstance) => {
  if (clientsWithCorsHandler.has(client)) {
    return;
  }
  clientsWithCorsHandler.add(client);

  client.interceptors.response.use(
    response => response,
    error => {
      notifyCorsError(error);
      return Promise.reject(error);
    },
  );
};

export const createQueryClientWithCorsHandler = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: handleQueryClientError,
    }),
    mutationCache: new MutationCache({
      onError: handleQueryClientError,
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isCorsOrNetworkError(error)) return false;
          return failureCount < 3;
        },
      },
    },
  });
